import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody, ISearchResponse } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { booleanLookup } from '../../../common/constants/common';
import {
  deleteO365ProjectUsage,
  getO365ProjectUsageById,
  saveO365ProjectUsage,
  searchO365ProjectUsage,
} from './o365ProjectUsage.action';
import { IO365ProjectUsageState } from './o365ProjectUsage.model';
import { IO365ProjectUsage } from '../../../services/o365/o365ProjectUsage/o365ProjectUsage.model';

export const initialState: IO365ProjectUsageState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: 'o365-project-usage',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const o365ProjectUsageSlice = createSlice({
  name: 'o365ProjectUsage',
  initialState,
  reducers: {
    clearO365ProjectUsage: () => {
      return initialState;
    },
    clearO365ProjectUsageMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearO365ProjectUsageGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchO365ProjectUsage.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchO365ProjectUsage.fulfilled.type]: (
      state,
      action: PayloadAction<ISearchResponse<IO365ProjectUsage>>
    ) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchO365ProjectUsage.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getO365ProjectUsageById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getO365ProjectUsageById.fulfilled.type]: (state, action: PayloadAction<IO365ProjectUsage>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getO365ProjectUsageById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveO365ProjectUsage.pending.type]: (state) => {
      state.save.loading = true;
    },
    [saveO365ProjectUsage.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload?.messages || [];
    },
    [saveO365ProjectUsage.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteO365ProjectUsage.pending.type]: (state) => {
      state.delete.loading = true;
    },
    [deleteO365ProjectUsage.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload?.messages || [];
    },
    [deleteO365ProjectUsage.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

export const {
  clearO365ProjectUsage,
  clearO365ProjectUsageMessages,
  clearO365ProjectUsageGetById,
  setTableColumnSelection,
} = o365ProjectUsageSlice.actions;

export const o365ProjectUsageSelector = (state: RootState) => state.o365ProjectUsage;

export default o365ProjectUsageSlice.reducer;
